import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom'; 
import { Search, X, Loader2 } from 'lucide-react';
import { SearchModalProps } from './types';
import { SearchResults } from './SearchResults';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';

export function SearchModal({
  isOpen,
  onClose,
  query,
  setQuery,
  results,
  loading,
  activeTab,
  setActiveTab,
  handleResultClick
}: SearchModalProps) {
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useOnClickOutside(searchRef, onClose);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

   // Adiciona um useEffect para desabilitar scroll no body quando o modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = ''; // Volta ao padrão
    }
    // Função de cleanup para garantir que o scroll volte ao normal
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);


  if (!isOpen) return null;

    return createPortal(
    <div className="fixed inset-0 bg-gray-900/50 !backdrop-blur-md z-[9999]" style={{margin: 0}} aria-modal="true" role="dialog">
      <div className="fixed inset-x-0 top-0 p-4 sm:p-6 sm:top-10 z-[10000]">
        <div
          ref={searchRef}
          className="mx-auto max-w-4xl overflow-hidden rounded-xl bg-white dark:bg-gray-900 shadow-2xl ring-1 ring-gray-500 dark:ring-gray-600"
        >
          <div className="flex items-center border-b border-gray-200 dark:border-gray-600 px-4">
            <Search className="h-5 w-5 text-gray-400 dark:text-gray-100" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar em todo o site..."
              className="flex-1 border-0 bg-transparent py-4 px-3 text-gray-900 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-0"
              aria-label="Campo de pesquisa"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 hover:bg-gray-100 rounded-full"
                aria-label="Limpar pesquisa"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            )}
            <button
              onClick={onClose}
              className="ml-2 flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-500 dark:text-gray-200"
              aria-label="Fechar pesquisa (ESC)"
            >
              ESC
            </button>
          </div>


          {query && (
            <div className="border-b border-gray-200 dark:border-gray-600">
              <nav className="flex overflow-x-auto px-4 scrollbar-hide">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === 'all'
                      ? 'text-teal-800 border-b-2 border-teal-800 dark:text-gray-200 dark:hover:text-gray-400'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-200 dark:hover:text-gray-400'
                  }`}
                >
                  Todos os resultados
                </button>
                <button
                  onClick={() => setActiveTab('documents')}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === 'documents'
                      ? 'text-teal-800 border-b-2 border-teal-800 dark:text-gray-200 dark:hover:text-gray-400'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-200 dark:hover:text-gray-400'
                  }`}
                >
                  Documentos
                </button>
                <button
                  onClick={() => setActiveTab('news')}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === 'news'
                      ? 'text-teal-800 border-b-2 border-teal-800 dark:text-gray-200 dark:hover:text-gray-400'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-200 dark:hover:text-gray-400'
                  }`}
                >
                  Notícias
                </button>
                <button
                  onClick={() => setActiveTab('biddings')}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === 'biddings'
                      ? 'text-teal-800 border-b-2 border-teal-800 dark:text-gray-200 dark:hover:text-gray-400'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-200 dark:hover:text-gray-400'
                  }`}
                >
                  Licitações
                </button>
                <button
                  onClick={() => setActiveTab('services')}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                    activeTab === 'services'
                     ? 'text-teal-800 border-b-2 border-teal-800 dark:text-gray-200 dark:hover:text-gray-400'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-200 dark:hover:text-gray-400'
                  }`}
                >
                  Serviços
                </button>
              </nav>
            </div>
          )}

              {/* Container de Resultados */}
          <div className="max-h-[75vh] overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-8" aria-label="Carregando resultados">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : query ? (
              <SearchResults
                results={results}
                handleResultClick={handleResultClick}
                activeTab={activeTab}
              />
            ) : (
              <p className="text-center py-8 text-gray-500">
                Digite algo para pesquisar...
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body // <-- O destino do portal
  );
}